import { Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useApplicationContext } from '../../../modules/context/ApplicationContext/ApplicationContext';
import { useOrderProductsContext } from '../../../modules/orders/contexts/OrderProducts.context';
import { OrderProduct } from '../../../modules/orders/products/orderProducts.types';
import { getFindProductGridKey } from '../../../services/modules/product-grid/product-grid.keys';
import useGeneralSettingsStore from '../../../stores/GeneralSettings/useGeneralSettingsStore';
import useOrderBookStore from '../../../stores/OrderBook/orderBook.store';
import useProductActionsStore from '../../../stores/ProductActionsStore/productActions.store';
import snackbarStore from '../../../stores/Snackbar.store';
import ProductsAddedCard from '../ProductsAddedCard/ProductsAddedCard';
import { removeNotifiedProducts, toProductsAddedCardItem } from '../productAddNotification.utils';
import { useProductNotificationStyles } from '../styles/ProductNotificationStyles';
import { useProductNotificationHighlight } from './useProductNotificationHighlight';

const PRODUCT_ADDED_SNACKBAR_ID = 'product-added';

const closeAddedNotification = () => {
  const { productNotificationState, closeProductNotification } = useProductActionsStore.getState();

  if (productNotificationState.productAdd) {
    closeProductNotification();
  }
};

export const useProductAddedSnackbar = () => {
  const { productNotificationState, orderProducts } = useProductActionsStore(
    useShallow((state) => ({
      productNotificationState: state.productNotificationState,
      orderProducts: state.orderProducts,
    })),
  );

  const { onClickRemove, pushOrderProducts, onClickRemoveKitFromOrder } = useOrderProductsContext();
  const mediaFormat = useGeneralSettingsStore((state) => state.generalSettings.mediaFormat);
  const { classes } = useProductNotificationStyles({ mediaFormat });
  const [expandedProducts, setExpandedProducts] = useState<OrderProduct[]>();
  const queryClient = useQueryClient();
  const { team } = useApplicationContext();
  const selectedOrder = useOrderBookStore((state) => state.selectedOrder);
  const removeActionsRef = useRef({ onClickRemove, onClickRemoveKitFromOrder });

  const isOpen = productNotificationState.productAdd && !!orderProducts?.length;
  const isExpanded = !!orderProducts && expandedProducts === orderProducts;

  useProductNotificationHighlight(isOpen);

  useEffect(() => {
    removeActionsRef.current = { onClickRemove, onClickRemoveKitFromOrder };
  });

  useEffect(
    () => () => {
      useProductActionsStore.getState().closeProductNotification();
      snackbarStore.close(PRODUCT_ADDED_SNACKBAR_ID);
    },
    [],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!orderProducts?.length || productNotificationState.isOrderProductsApplied) {
      return;
    }

    orderProducts.forEach((product) => {
      queryClient.removeQueries({
        queryKey: [
          team?.id,
          ...getFindProductGridKey({
            productColorId: product.productColorId,
            availability: product.availability,
            deliveryWindowId: product?.deliveryWindow?.id ?? '',
            orderId: selectedOrder?.id,
          }),
        ],
      });
    });
    pushOrderProducts(orderProducts);
  }, [orderProducts, queryClient.removeQueries, selectedOrder?.id, team?.id]);

  useEffect(() => {
    if (!productNotificationState.productAdd || !orderProducts?.length) {
      snackbarStore.close(PRODUCT_ADDED_SNACKBAR_ID);
      return;
    }

    const handleUndo = () => {
      const { closeProductNotification, resetProductActionsStore } =
        useProductActionsStore.getState();

      closeProductNotification();
      removeNotifiedProducts({
        orderProducts,
        removeProduct: removeActionsRef.current.onClickRemove,
        removeKit: removeActionsRef.current.onClickRemoveKitFromOrder,
      });
      resetProductActionsStore();
    };

    snackbarStore.showCustom({
      id: PRODUCT_ADDED_SNACKBAR_ID,
      isPersistent: isExpanded,
      onClose: closeAddedNotification,
      content: (
        <Box className={classes.snackbar}>
          <ProductsAddedCard
            items={orderProducts.map(toProductsAddedCardItem)}
            mediaFormat={mediaFormat}
            isExpanded={isExpanded}
            onExpandedChange={(shouldExpand) =>
              setExpandedProducts(shouldExpand ? orderProducts : undefined)
            }
            onUndo={handleUndo}
            onClose={closeAddedNotification}
          />
        </Box>
      ),
    });
  }, [productNotificationState, orderProducts, isExpanded, mediaFormat, classes]);
};
